import { GAME_RESULT_VARIANT } from '@/constants/maze';
import { liveGameEngine } from '@/features/live-engine/live-game-engine';
import { useDemoStore } from '@/store/demo';
import { useLiveStore } from '@/store/live';
import { usePreviewStore } from '@/store/preview';
import { MazeGamePhase } from '@/types/maze-phase';
import { MazePlayMode, isMazePlayModePreview } from '@/utils/maze/maze-play-mode';
import { getConfiguredUiStageId } from '@/utils/stage/stage-utils';
import { secondsRemainingUntil } from '@/utils/time/expiry-timer';
import { useEffect, useRef } from 'react';
import { useShallow } from 'zustand/react/shallow';

function pickByPlayMode<T>(
  isDemo: boolean,
  isPreview: boolean,
  demoValue: T,
  previewValue: T,
  liveValue: T,
): T {
  if (isDemo) {
    return demoValue;
  }
  if (isPreview) {
    return previewValue;
  }
  return liveValue;
}

export function useMazeTimer(mode: MazePlayMode): void {
  const isDemo = mode === MazePlayMode.Demo;
  const isPreview = isMazePlayModePreview(mode);
  const demoPhase = useDemoStore((s) => s.phase);
  const livePhase = useLiveStore((s) => s.phase);
  const previewPhase = usePreviewStore((s) => s.phase);
  const phase = pickByPlayMode(isDemo, isPreview, demoPhase, previewPhase, livePhase);

  const demoTimer = useDemoStore((s) => s.timer);
  const liveTimer = useLiveStore((s) => s.timer);
  const previewTimer = usePreviewStore((s) => s.timer);
  const timer = pickByPlayMode(isDemo, isPreview, demoTimer, previewTimer, liveTimer);

  const demoDecrement = useDemoStore((s) => s.decrementTimer);
  const liveDecrement = useLiveStore((s) => s.decrementTimer);
  const previewDecrement = usePreviewStore((s) => s.decrementTimer);
  const decrementTimer = pickByPlayMode(
    isDemo,
    isPreview,
    demoDecrement,
    previewDecrement,
    liveDecrement,
  );

  const demoSetTimer = useDemoStore((s) => s.setTimer);
  const liveSetTimer = useLiveStore((s) => s.setTimer);
  const previewSetTimer = usePreviewStore((s) => s.setTimer);
  const setTimer = pickByPlayMode(isDemo, isPreview, demoSetTimer, previewSetTimer, liveSetTimer);

  const hasLiveSession = useLiveStore((s) => s.hasActiveLiveSession);
  const liveSessionExpiryAt = useLiveStore((s) => s.session?.expiryAt);
  const { endGameEarly, publishTerminalResult } = useLiveStore(
    useShallow((s) => ({
      endGameEarly: s.endGameEarly,
      publishTerminalResult: s.publishTerminalResult,
    })),
  );

  useEffect(() => {
    if (isDemo) {
      return;
    }
    if (isPreview) {
      return;
    }

    if (phase !== MazeGamePhase.PLAYING) {
      return;
    }

    if (hasLiveSession && !liveGameEngine.isTimerEnabled()) {
      return;
    }

    if (hasLiveSession) {
      const tick = () => {
        if (liveSessionExpiryAt) {
          setTimer(secondsRemainingUntil(liveSessionExpiryAt));
        }
      };
      tick();
      const intervalId = globalThis.setInterval(tick, 1000);
      return () => {
        globalThis.clearInterval(intervalId);
      };
    }

    const intervalId = globalThis.setInterval(() => {
      decrementTimer();
    }, 1000);

    return () => {
      globalThis.clearInterval(intervalId);
    };
  }, [decrementTimer, hasLiveSession, isDemo, isPreview, liveSessionExpiryAt, phase, setTimer]);

  const liveTimeUpHandledRef = useRef(false);

  useEffect(() => {
    if (isDemo) {
      return;
    }
    if (isPreview) {
      return;
    }

    if (hasLiveSession && !liveGameEngine.isTimerEnabled()) {
      return;
    }

    if (phase !== MazeGamePhase.PLAYING || timer > 0) {
      liveTimeUpHandledRef.current = false;
      return;
    }

    if (liveTimeUpHandledRef.current) {
      return;
    }
    liveTimeUpHandledRef.current = true;

    const stageId = getConfiguredUiStageId();
    if (hasLiveSession) {
      void endGameEarly({ refreshStatus: false })
        .catch(() => {})
        .finally(() => {
          publishTerminalResult(stageId, GAME_RESULT_VARIANT.FAILURE);
        });
      return;
    }
    publishTerminalResult(stageId, GAME_RESULT_VARIANT.FAILURE);
  }, [endGameEarly, hasLiveSession, isDemo, isPreview, phase, publishTerminalResult, timer]);
}
